// Content for the public /advertise page. Formats and partner apps follow the sponsor
// catalog (src/lib/sponsor/catalog.ts). Dashboard numbers are examples, not real data.

export type AdFormatId = 'banner' | 'native' | 'fullscreen' | 'survey' | 'reward' | 'push';

export interface AdFormat {
  id: AdFormatId;
  name: string;
  summary: string;
  points: [string, string];
  /** Which plans include the format (sponsor catalog). */
  plans: string;
}

export const adFormats: AdFormat[] = [
  {
    id: 'banner',
    name: 'Home banner',
    summary: 'Your banner on the home screen of a partner app, the first thing members see when they open it.',
    points: [
      'Fixed banner at the top of the home screen, on phone and web.',
      'Sizes 320×100 and 728×90. Upload one image and we fit both.',
    ],
    plans: 'All plans',
  },
  {
    id: 'native',
    name: 'Native feed post',
    summary: 'An ad that looks like a normal post in the app feed, with your logo, image and button.',
    points: [
      'Reads like content, so members scroll to it instead of past it.',
      'Matches the partner app’s fonts and colors automatically.',
    ],
    plans: 'Growth and Premium',
  },
  {
    id: 'survey',
    name: 'Sponsored survey',
    summary: 'Ask members your own questions in the Survey app. They earn points for answering.',
    points: [
      'Real answers from real members, with your brand at the top.',
      'Growth includes 500 answers a month; results land in your dashboard.',
    ],
    plans: 'Growth and Premium',
  },
  {
    id: 'reward',
    name: 'Logo on a reward',
    summary: 'Put your logo on a reward members redeem with their points, like a coffee or a phone top-up.',
    points: [
      'Members remember the brand that gave them something.',
      'Fund the reward from your points budget; we track every redemption.',
    ],
    plans: 'Growth and Premium',
  },
  {
    id: 'fullscreen',
    name: 'Full-screen ad',
    summary: 'A full-screen ad shown once when members open the app, with a close button after 3 seconds.',
    points: [
      'The biggest canvas: great for launches and big campaigns.',
      'Shown at most once a day per member, so it never feels spammy.',
    ],
    plans: 'Premium, 7 days a month',
  },
  {
    id: 'push',
    name: 'Push message',
    summary: 'A notification on the member’s phone with your offer, opened straight into the app.',
    points: [
      'Reaches members even when the app is closed.',
      'Only members who allowed notifications receive it.',
    ],
    plans: 'Premium, 2 a month',
  },
];

export const partnerApps = [
  { id: 'survey', name: 'Survey app', color: '#2f51d6', icon: 'ri-survey-fill', tagline: 'Members answer surveys and earn points' },
  { id: 'idi', name: 'IDI app', color: '#0e9f7e', icon: 'ri-shield-user-fill', tagline: 'Digital ID and member services' },
] as const;

export type PartnerAppId = (typeof partnerApps)[number]['id'];

export const advertiseSteps = [
  { icon: 'ri-user-add-fill', title: 'Register', text: 'Create your advertiser account and pick a plan.' },
  { icon: 'ri-layout-masonry-fill', title: 'Choose formats and apps', text: 'Banner, feed post, survey and more, on the partner apps you want.' },
  { icon: 'ri-image-add-fill', title: 'Upload your ad', text: 'Add your image, text and link. We review it within 1 business day.' },
  { icon: 'ri-line-chart-fill', title: 'Track results', text: 'See views, clicks and spend for every ad in your dashboard.' },
];

// Example dashboard (clearly labelled as an example on the page).
export const demoCampaigns = [
  { name: 'Year-end promo', format: 'Home banner', app: 'Survey app', views: 48210, clicks: 1302, status: 'Running' },
  { name: 'New card launch', format: 'Native feed post', app: 'IDI app', views: 31870, clicks: 1147, status: 'Running' },
  { name: 'Customer feedback', format: 'Sponsored survey', app: 'Survey app', views: 9450, clicks: 612, status: 'In review' },
] as const;

export const demoDaily = [
  { day: 'Mon', views: 11200, clicks: 320 },
  { day: 'Tue', views: 12900, clicks: 371 },
  { day: 'Wed', views: 12100, clicks: 352 },
  { day: 'Thu', views: 14300, clicks: 433 },
  { day: 'Fri', views: 15800, clicks: 498 },
  { day: 'Sat', views: 13400, clicks: 455 },
  { day: 'Sun', views: 9830, clicks: 332 },
];
